import React, { useEffect, useMemo, useState } from 'react';
import { useAgentConnected } from './AgentStatus';
import { Link } from 'react-router-dom';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { shortDate } from '../reach/reach';
import { useAccount } from '../../context/AccountContext';
import {
  GET_TINYCMS_DELETED_FORMS,
  GET_TINYCMS_FORM,
  GET_TINYCMS_FORM_RESPONSES,
  GET_TINYCMS_FORM_SETTINGS,
  GET_TINYCMS_FORMS,
  GET_TINYCMS_UNREAD_RESPONSES,
  GET_USER_PROFILE,
} from '../../graphql';
import {
  DELETE_TINYCMS_FORM,
  DELETE_TINYCMS_FORM_RESPONSE,
  MARK_TINYCMS_FORM_RESPONSES_READ,
  RESTORE_TINYCMS_FORM,
  SET_TINYCMS_FORM_ALERT,
} from '../../graphql/mutations';
import { showInkToast } from './InkToast';
import { pageStatus, RailSkeleton } from './CmsPages';
import { RecentlyDeleted, type DeletedItem } from './RecentlyDeleted';
import {
  type FormField,
  type FormRow,
  type ResponseRow,
  placedLabel,
  receivedLabel,
  responsesCsv,
} from './formResponses';
import { ApprovalHistory, Finding, FindingsList, PublishPanel } from './shared';
import { Loader } from '../common/PageLoader';
import { askInk } from './InkConfirm';
import { openAskAgent } from './AskYourAgent';

/**
 * Content → Forms ("tinycms UX optimization review v7", Prototype v3): the
 * site's forms in a rail; the one picked as a read-only preview (Form) or
 * what readers sent through it (Responses), with the way to publish it when
 * the agent has drafted or changed it.
 *
 * The agent builds forms (MCP create_form / update_form) and places them on
 * pages; a member publishes them here. Responses are user-generated content
 * kept for the owner — readable only here (never emailed, never over MCP),
 * kept as long as Form settings says.
 *
 * A form can be deleted here (off the site if live; its responses are kept)
 * and restored from "Recently deleted" for 30 days, like pages and articles.
 */

const PAGE_SIZE = 50;
const RESTORE_NOTE =
  'Restored forms come back as drafts with their responses. Publish one again, on a page, to take responses again.';
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** `fix`: arrived from Today's Fix on `initial`, so its checks open with the open ones marked. */
export function CmsFormsView({
  initial,
  fix = false,
  openResponses = false,
}: {
  initial?: string | null;
  fix?: boolean;
  /** Open the linked form on its Responses (Today's "N new form responses"). */
  openResponses?: boolean;
}) {
  const { accountId } = useAccount();
  const { data, loading, refetch } = useQuery(GET_TINYCMS_FORMS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: deletedData, refetch: refetchDeleted } = useQuery(GET_TINYCMS_DELETED_FORMS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const deleted: DeletedItem[] = deletedData?.tinycmsDeletedForms || [];
  const forms: FormRow[] = useMemo(() => data?.tinycmsForms || [], [data]);
  const [sel, setSel] = useState<string | null>(initial || null);
  // Bumped after a delete, so Recently deleted opens on what just went (Round 36).
  const [openDeleted, setOpenDeleted] = useState(0);
  const onRestored = (id: string) => {
    const name = deleted.find((d) => d.id === id)?.title;
    void refetch();
    void refetchDeleted();
    setSel(id);
    showInkToast(`${name || 'The form'} restored as a draft.`);
  };

  // Grey bars until the list is in — also before the site is known, when the
  // query hasn't run, so "No forms yet" never flashes first (QA round 7).
  if (!data && (loading || !accountId))
    return <RailSkeleton label="Loading forms" groups={[['FORMS', [120, 96, 140]]]} />;
  if (!forms.length) {
    return (
      <div className="pg-empty">
        <div className="pg-empty-title">No forms yet</div>
        <p>
          Ask your agent for one: “Add a contact form to the About page”, or “Put a newsletter
          sign-up on the home page”. It appears here for you to publish, and what readers send shows
          up here too.
        </p>
        {accountId ? (
          <div style={{ marginTop: 20, textAlign: 'left' }}>
            <RecentlyDeleted
              accountId={accountId}
              items={deleted}
              restoreMutation={RESTORE_TINYCMS_FORM}
              onRestored={onRestored}
              note={RESTORE_NOTE}
            />
          </div>
        ) : null}
      </div>
    );
  }

  const form = forms.find((f) => f.id === sel) || forms[0];
  return (
    <div className="pg">
      <aside className="pg-rail">
        <div>
          <div className="pg-rail-head">FORMS</div>
          {forms.map((f) => {
            const st = pageStatus(f.state);
            return (
              <button
                key={f.id}
                type="button"
                className="pg-rail-item"
                data-active={form.id === f.id ? 'true' : undefined}
                aria-current={form.id === f.id ? 'page' : undefined}
                onClick={() => setSel(f.id)}
              >
                <span className="pg-dot" style={{ background: st.dot }} />
                <span className="pg-rail-text">
                  <span className="pg-rail-title">{f.name}</span>
                  <span className="pg-rail-sub">
                    {/* A restored form, or any draft nowhere yet (Round 36). */}
                    {!f.state.live && !f.placedOn.length
                      ? 'Draft · not on a page'
                      : placedLabel(f.placedOn)}
                  </span>
                </span>
                <span
                  className="fm-count"
                  data-unread={f.responses.unread ? 'true' : undefined}
                  title={
                    f.responses.unread
                      ? `${plural(f.responses.unread, 'new response')}`
                      : plural(f.responses.total, 'response')
                  }
                >
                  {f.responses.unread ? `${f.responses.unread} new` : f.responses.total}
                </span>
              </button>
            );
          })}
        </div>
        {accountId ? (
          // Collapsed with a count, remembered, as on Pages (Round 36).
          <RecentlyDeleted
            accountId={accountId}
            items={deleted}
            restoreMutation={RESTORE_TINYCMS_FORM}
            onRestored={onRestored}
            note={RESTORE_NOTE}
            collapsible="tinycms.forms.deletedOpen"
            openWhen={openDeleted}
          />
        ) : null}
      </aside>
      <div className="pg-main">
        <FormDetail
          key={form.id}
          row={form}
          focus={fix && form.id === initial}
          openResponses={openResponses && form.id === initial}
          onChanged={() => void refetch()}
          onDeleted={() => {
            setSel(null);
            void refetch();
            void refetchDeleted();
            setOpenDeleted((n) => n + 1);
          }}
        />
      </div>
    </div>
  );
}

function FormDetail({
  row,
  focus = false,
  openResponses = false,
  onChanged,
  onDeleted,
}: {
  row: FormRow;
  focus?: boolean;
  openResponses?: boolean;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  // "Your agent drafted this" only when the site has an agent (nothing records who did).
  const agentConnected = useAgentConnected();
  const { accountId } = useAccount();
  const [deleteForm, { loading: deleting }] = useMutation(DELETE_TINYCMS_FORM);
  const liveOn = row.livePlacedOn || [];
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [tab, setTab] = useState<'form' | 'responses'>(
    openResponses || row.responses.unread ? 'responses' : 'form'
  );
  const [checksOpen, setChecksOpen] = useState(focus);
  const st = pageStatus(row.state);
  const live = Boolean(row.state.live);
  const waiting = !live || row.state.hasUnpublishedChanges;
  const unplaced = row.placedOn.length === 0;

  // Pages that place it only in a draft: deleting takes it off them too (Round 36).
  const draftOnly = row.placedOn.filter((p) => !liveOn.some((l) => l.id === p.id));

  const handleDelete = async () => {
    setDeleteError(null);
    const scheduled = Boolean(row.state.scheduled);
    const n = row.responses.total;
    const ok = await askInk({
      title: `Delete ${row.name}?`,
      body: `It moves to Recently deleted${n ? ` with its ${plural(n, 'response')}` : ''}. After 30 days it’s gone for good.${
        live || scheduled
          ? ` It’s ${live ? 'live' : 'scheduled'}, so it comes off your site now.`
          : ''
      }`,
      note: draftOnly.length
        ? `It also comes off the ${draftOnly.map((p) => p.title).join(', ')} ${draftOnly.length === 1 ? 'draft' : 'drafts'}.`
        : undefined,
      confirmLabel: 'Delete form',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteForm({ variables: { accountId, id: row.id } });
      showInkToast(`${row.name} moved to Recently deleted.`);
      onDeleted();
    } catch (err) {
      // e.g. a live page still shows it: the server says which page to change.
      setDeleteError(err instanceof Error ? err.message : 'Couldn’t delete the form');
    }
  };

  const openChecks = () => {
    setChecksOpen(true);
    setTimeout(
      () => document.getElementById('fm-checks')?.scrollIntoView({ behavior: 'smooth' }),
      0
    );
  };

  return (
    <>
      <div className="pg-head">
        <div style={{ minWidth: 0 }}>
          <div className="pg-title">{row.name}</div>
          <div className="pg-meta">
            <span className="pg-status" style={{ color: st.color }}>
              <span className="pg-dot" style={{ background: st.dot }} />
              {st.label}
            </span>
            <span>·</span>
            {/* Its type picks the small print (Form settings); your agent can change it. */}
            <span title="Picks the small print readers see under it (Form settings)">
              {row.purpose === 'signup' ? 'Sign-up form' : 'Contact form'}
            </span>
            <span>·</span>
            {row.placedOn.length ? (
              <span>
                On{' '}
                {row.placedOn.map((p, i) => (
                  <React.Fragment key={p.id}>
                    {i ? ', ' : ''}
                    <Link to={`/content?view=pages&page=${p.id}`} className="pg-url">
                      {p.title}
                    </Link>
                  </React.Fragment>
                ))}
              </span>
            ) : (
              <span className="pg-url" data-muted="true">
                Not on a page yet
              </span>
            )}
            <span>·</span>
            <span>
              {/* Who made it and when it last changed, as the design writes it:
                  "3 fields · Agent · Oct 20" (backlog db0cafd8). */}
              {[
                plural(row.fieldCount, 'field'),
                row.addedByTemplate ? 'Template' : row.madeByAgent ? 'Agent' : null,
                shortDate(row.updatedAt),
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
        </div>
        <div className="pg-seg" role="tablist" aria-label="Form or responses">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'form'}
            data-active={tab === 'form' ? 'true' : undefined}
            onClick={() => setTab('form')}
          >
            Form
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'responses'}
            data-active={tab === 'responses' ? 'true' : undefined}
            onClick={() => setTab('responses')}
          >
            Responses · {row.responses.total}
          </button>
        </div>
      </div>

      {waiting ? (
        <div className="pg-action">
          <span
            className="pg-dot"
            style={{ background: 'var(--ink-amber)', width: 8, height: 8 }}
          />
          <div className="pg-action-text">
            <div className="pg-action-title">
              {/* Who made it, when that's known: a template's Look and pages is
                  the person's own click, not an agent (backlog 724547f8). */}
              {!live && row.addedByTemplate
                ? `Added by the ${row.addedByTemplate} template${unplaced ? ' · Not on a page yet' : ''}`
                : agentConnected
                  ? live
                    ? 'Your agent changed this form'
                    : 'Your agent drafted this form'
                  : live
                    ? 'This form has changes not published yet'
                    : 'This form isn’t published yet'}
            </div>
            <div className="pg-action-note">
              {live
                ? 'Readers still see the published version until you publish.'
                : unplaced
                  ? 'Readers can’t see it until it’s on a page. Put it on one, or delete it if you don’t need it.'
                  : 'Readers can’t see it yet. Publish it with the page it’s on.'}
            </div>
          </div>
          {!live && unplaced ? (
            <>
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                onClick={() => openAskAgent({ action: 'formPlace', ctx: { title: row.name } })}
              >
                Put it on a page
              </button>
              <button
                type="button"
                className="ink-btn"
                style={{ color: 'var(--ink-red)' }}
                disabled={deleting}
                onClick={() => void handleDelete()}
              >
                Delete it
              </button>
            </>
          ) : (
            <button type="button" className="ink-btn ink-btn-primary" onClick={openChecks}>
              Review and publish
            </button>
          )}
        </div>
      ) : null}

      <EmailMeRow row={row} onChanged={onChanged} />

      {tab === 'form' ? (
        <FormPreview id={row.id} />
      ) : (
        <ResponsesPane row={row} onChanged={onChanged} />
      )}

      <div className="pg-more">
        <button
          type="button"
          className="pg-more-toggle"
          aria-expanded={checksOpen}
          onClick={() => setChecksOpen((v) => !v)}
        >
          {checksOpen ? '▾' : '▸'} Checks and history
        </button>
        {checksOpen ? <FormChecks id={row.id} onChanged={onChanged} focus={focus} /> : null}
      </div>

      <div className="pg-delete">
        {/* Disabled exactly when the server would refuse (a live page shows it), like Delete page. */}
        {/* Red outline, as Delete author (Round 36). */}
        <button
          type="button"
          className="ink-btn pg-delete-btn"
          disabled={deleting || liveOn.length > 0}
          title={liveOn.length ? 'Take it off the live page first' : undefined}
          onClick={() => void handleDelete()}
        >
          {deleting ? 'Deleting…' : 'Delete form'}
        </button>
        <span className="pg-note pg-delete-note">
          {liveOn.length
            ? `A form a live page shows can’t be deleted. Ask your agent to take it off ${liveOn
                .map((p) => p.title)
                .join(', ')} first.`
            : 'It moves to Recently deleted for 30 days, with its responses. You can restore it as a draft until then.'}
        </span>
        {deleteError ? (
          <span role="alert" style={{ color: 'var(--ink-red)', fontSize: 13 }}>
            {deleteError}
          </span>
        ) : null}
      </div>
    </>
  );
}

/** The working copy as readers will see it, in the app's own styles; read-only. */
function FormPreview({ id }: { id: string }) {
  const { accountId } = useAccount();
  const { data, loading } = useQuery(GET_TINYCMS_FORM, {
    variables: { accountId, id },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: settingsData } = useQuery(GET_TINYCMS_FORM_SETTINGS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  const doc = data?.tinycmsForm?.content;
  // The form's own consent line (another language) wins over the site's line
  // for its type: sign-up forms get the sign-up line, as the live site does.
  const settings = settingsData?.tinycmsFormSettings;
  const siteConsent: string | null =
    (data?.tinycmsForm?.purpose === 'signup' ? settings?.signupConsent : settings?.consent) || null;
  if (loading && !doc) return <Loader label="Loading" className="ink-muted" />;
  if (!doc) return null;
  const inline = doc.layout === 'inline';
  const consent: string | null = doc.consent || siteConsent;
  return (
    <>
      <div className="fm-preview" data-layout={inline ? 'inline' : 'stacked'}>
        {doc.heading ? <div className="fm-preview-heading">{doc.heading}</div> : null}
        {doc.intro ? <div className="fm-preview-intro">{doc.intro}</div> : null}
        <div className="fm-preview-fields">
          {(doc.fields as FormField[]).map((f) => (
            <div key={f.name} className="fm-preview-field" data-type={f.type}>
              {f.type === 'checkbox' ? (
                <span className="fm-preview-check">
                  <span className="pg-box" /> {f.label}
                </span>
              ) : f.type === 'checkboxes' ? (
                // One question, tick any: the choices as boxes under it.
                <>
                  <span className="fm-preview-label">
                    {f.label}
                    {f.required ? ' *' : ''}
                  </span>
                  <span className="fm-preview-choices">
                    {(f.options || []).map((o) => (
                      <span key={o} className="fm-preview-check">
                        <span className="pg-box" /> {o}
                      </span>
                    ))}
                  </span>
                </>
              ) : (
                <>
                  {!inline ? (
                    <span className="fm-preview-label">
                      {f.label}
                      {f.required ? ' *' : ''}
                    </span>
                  ) : null}
                  <span
                    className="fm-preview-input"
                    data-tall={f.type === 'textarea' ? 'true' : undefined}
                  >
                    {f.type === 'select'
                      ? `${f.placeholder || 'Choose one'} ▾`
                      : f.placeholder || (inline ? f.label : '')}
                  </span>
                </>
              )}
              {f.help && !inline ? <span className="fm-preview-help">{f.help}</span> : null}
            </div>
          ))}
          <span className="fm-preview-button">{doc.submitLabel || 'Send'}</span>
        </div>
        {consent ? <div className="fm-preview-consent">{consent}</div> : null}
      </div>
      <p className="pg-note">
        Read-only preview. To change fields or wording, ask your agent.
        {doc.successPath ? ` After sending, readers go to ${doc.successPath}.` : ''}
      </p>
    </>
  );
}

/**
 * "Email me new responses" (Prototype v14), for the person looking: off by
 * default. The email only says how many are waiting and links here — it never
 * carries what readers sent (the privacy policy: responses stay in the app).
 */
function EmailMeRow({ row, onChanged }: { row: FormRow; onChanged: () => void }) {
  const { accountId } = useAccount();
  const { data } = useQuery<{ me?: { email?: string } }>(GET_USER_PROFILE);
  const [setAlert, { loading }] = useMutation(SET_TINYCMS_FORM_ALERT);
  const on = Boolean(row.emailMe);
  const email = data?.me?.email || 'you';
  const toggle = async () => {
    try {
      await setAlert({ variables: { accountId, formId: row.id, on: !on } });
      showInkToast(
        on
          ? `You won’t get emails for ${row.name}.`
          : `You’ll get an email at ${email} when ${row.name} gets responses.`
      );
      onChanged();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    }
  };
  return (
    <div className="fm-mail">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="fm-mail-title">Email me new responses</div>
        <div className="fm-mail-note">
          {on
            ? `On · an email to ${email} when responses arrive, at most every 10 minutes. It says how many, never what they say.`
            : 'Off · responses only show under Responses'}
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label="Email me new responses"
        className="fm-mail-switch"
        data-on={on ? 'true' : undefined}
        disabled={loading}
        onClick={() => void toggle()}
      >
        <span />
      </button>
    </div>
  );
}

function ResponsesPane({ row, onChanged }: { row: FormRow; onChanged: () => void }) {
  const { accountId } = useAccount();
  const apollo = useApolloClient();
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [open, setOpen] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const { data, loading, refetch } = useQuery(GET_TINYCMS_FORM_RESPONSES, {
    variables: { accountId, formId: row.id, limit, offset: 0 },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: formData } = useQuery(GET_TINYCMS_FORM, {
    variables: { accountId, id: row.id },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  const [markRead] = useMutation(MARK_TINYCMS_FORM_RESPONSES_READ);
  const [remove, { loading: removing }] = useMutation(DELETE_TINYCMS_FORM_RESPONSE);
  const fields: FormField[] = formData?.tinycmsForm?.content?.fields || [];
  const result = data?.tinycmsFormResponses;
  const responses: ResponseRow[] = result?.responses || [];
  const total: number = result?.total ?? row.responses.total;
  const unread: number = result?.unread ?? row.responses.unread;

  // Two columns of the form after Received: its first fields a person fills in.
  const shown = fields.filter((f) => f.type !== 'checkbox' && f.type !== 'checkboxes').slice(0, 2);
  const grid = {
    gridTemplateColumns: `130px ${shown.map(() => 'minmax(0, 1fr)').join(' ')} 110px`,
  };

  const changed = async () => {
    await refetch();
    onChanged();
    // The Content badge and the Forms tab count.
    void apollo.refetchQueries({ include: [GET_TINYCMS_UNREAD_RESPONSES, GET_TINYCMS_FORMS] });
  };

  const toggle = async (r: ResponseRow) => {
    setOpen((cur) => (cur === r.id ? null : r.id));
    if (!r.read) {
      await markRead({ variables: { accountId, formId: row.id, ids: [r.id] } });
      await changed();
    }
  };

  const markAll = async () => {
    await markRead({ variables: { accountId, formId: row.id } });
    await changed();
  };

  const del = async (r: ResponseRow) => {
    if (
      !(await askInk({
        title: 'Delete this response for good?',
        body: 'It can’t be brought back.',
        confirmLabel: 'Delete',
        danger: true,
      }))
    )
      return;
    setError('');
    try {
      await remove({ variables: { accountId, id: r.id } });
      setOpen(null);
      await changed();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Couldn’t delete the response');
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    setError('');
    try {
      const all: ResponseRow[] = [];
      for (let offset = 0; offset < total; offset += 1000) {
        const { data: page } = await apollo.query({
          query: GET_TINYCMS_FORM_RESPONSES,
          variables: { accountId, formId: row.id, limit: 1000, offset },
          fetchPolicy: 'network-only',
        });
        const got: ResponseRow[] = page?.tinycmsFormResponses?.responses || [];
        all.push(...got);
        if (got.length < 1000) break;
      }
      const blob = new Blob([responsesCsv(fields, all)], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${row.name.replace(/[^\w-]+/g, '-').toLowerCase() || 'form'}-responses.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Couldn’t export the responses');
    } finally {
      setExporting(false);
    }
  };

  const cellFor = (r: ResponseRow, f: FormField) =>
    r.fields.find((v) => v.name === f.name)?.value || '';

  return (
    <div className="fm-responses">
      <div className="fm-resp-bar">
        <span className="fm-resp-total">
          {plural(total, 'response')}
          {unread ? <span className="fm-resp-new"> · {unread} new</span> : null}
        </span>
        {total > responses.length && responses.length ? (
          <span className="ink-muted">Showing the latest {responses.length}</span>
        ) : null}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {unread ? (
            <button type="button" className="ink-btn ink-btn-quiet" onClick={() => void markAll()}>
              Mark all read
            </button>
          ) : null}
          <button
            type="button"
            className="ink-btn ink-btn-quiet"
            disabled={!total || exporting}
            onClick={() => void exportCsv()}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </span>
      </div>
      {error ? <p className="text-xs text-error">{error}</p> : null}

      {loading && !responses.length ? (
        <Loader label="Loading" className="ink-muted" />
      ) : !responses.length ? (
        <p className="fm-resp-empty">
          {row.state.live
            ? row.placedOn.length
              ? 'No responses yet.'
              : 'No responses yet. Ask your agent to put this form on a page.'
            : 'No responses yet. Readers can send this form once it’s published.'}
        </p>
      ) : (
        <div className="fm-table" role="table" aria-label="Responses">
          <div className="fm-row fm-row-head" role="row" style={grid}>
            <span role="columnheader">Received</span>
            {shown.map((f) => (
              <span key={f.name} role="columnheader">
                {f.label}
              </span>
            ))}
            <span role="columnheader">Page</span>
          </div>
          {responses.map((r) => (
            <React.Fragment key={r.id}>
              <button
                type="button"
                className="fm-row"
                role="row"
                style={grid}
                data-unread={r.read ? undefined : 'true'}
                aria-expanded={open === r.id}
                onClick={() => void toggle(r)}
              >
                <span role="cell">
                  {!r.read ? <span className="fm-unread-dot" aria-label="New" /> : null}
                  {receivedLabel(r.receivedAt)}
                </span>
                {shown.map((f) => (
                  <span key={f.name} role="cell" className="fm-cell">
                    {cellFor(r, f)}
                  </span>
                ))}
                <span role="cell" className="ink-muted">
                  {[r.page, r.via === 'chat' ? 'via chat' : null].filter(Boolean).join(' · ')}
                </span>
              </button>
              {open === r.id ? (
                <div className="fm-detail">
                  <dl>
                    {r.fields.map((v) => (
                      <React.Fragment key={v.name}>
                        <dt>{v.label}</dt>
                        <dd>{v.value}</dd>
                      </React.Fragment>
                    ))}
                    <dt>Received</dt>
                    <dd>{format(new Date(r.receivedAt), 'MMM d, yyyy h:mm a')}</dd>
                    {r.via === 'chat' ? (
                      <>
                        <dt>Sent</dt>
                        <dd>Through the chat, a question at a time</dd>
                      </>
                    ) : null}
                  </dl>
                  <button
                    type="button"
                    className="ink-btn ink-btn-quiet"
                    disabled={removing}
                    onClick={() => void del(r)}
                  >
                    Delete response
                  </button>
                </div>
              ) : null}
            </React.Fragment>
          ))}
          {total > responses.length ? (
            <button
              type="button"
              className="ink-btn ink-btn-quiet"
              style={{ marginTop: 12 }}
              onClick={() => setLimit((n) => n + PAGE_SIZE)}
            >
              Show {Math.min(PAGE_SIZE, total - responses.length)} more
            </button>
          ) : null}
        </div>
      )}
      {/* Under the table, empty or not (Prototype, backlog 477f216a). */}
      <p className="fm-resp-privacy">Prism AI never reads forms or their responses.</p>
    </div>
  );
}

/** A form's checks, publish controls and approval history. `focus` marks the open checks (Today → Fix). */
function FormChecks({
  id,
  onChanged,
  focus = false,
}: {
  id: string;
  onChanged: () => void;
  focus?: boolean;
}) {
  const { accountId } = useAccount();
  const { data, loading, refetch } = useQuery(GET_TINYCMS_FORM, {
    variables: { accountId, id },
    skip: !accountId,
    fetchPolicy: 'network-only',
  });
  const form = data?.tinycmsForm;
  const loaded = Boolean(form);
  useEffect(() => {
    if (focus && loaded) {
      document.getElementById('fm-checks')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [focus, loaded]);
  if (loading && !form) return <Loader label="Loading" className="ink-site-row-desc" />;
  if (!form) return null;
  const findings: Finding[] = form.state.preflight || [];
  const blocking = findings.filter((f) => f.severity === 'blocking').length;
  return (
    <div id="fm-checks" className="pg-checks">
      <div>
        <p className="ink-site-kicker">Checks</p>
        <FindingsList findings={findings} empty="No findings." focus={focus} />
      </div>
      <div>
        <p className="ink-site-kicker">Publish</p>
        <PublishPanel
          kind="form"
          id={id}
          workingHash={form.workingHash}
          state={form.state}
          blocking={blocking}
          onDone={() => {
            void refetch();
            onChanged();
          }}
        />
      </div>
      <div>
        <p className="ink-site-kicker">History</p>
        <ApprovalHistory kind="form" subjectId={id} limit={10} />
      </div>
    </div>
  );
}
